import { useTranslation } from 'react-i18next'
import { Expander, ExpanderHeader, ExpanderContent, Divider, DialogOverlay, colors } from '@fs/zion-ui'
import React, { useMemo, useState } from 'react'
import { css } from '@emotion/core'
import PropTypes from 'prop-types'
import DmcCopyToClipboardTooltip from '../../util/DmcCopyToClipboardTooltip'
import MoreMetadataButton from './buttons/MoreMetadataButton'
import JSONTable from '../app/JSON'

const breakpoints = [600, 800, 1000]

const mq = breakpoints.map((bp) => `@media (min-width: ${bp}px)`)

const border = css`
  border: 1px solid #d7d7d7;
  box-shadow: 0px 1px #bfbfbf;
  border-radius: 5px;
  padding: 0em 1em;
  user-select: text;
`
const flexContainerNoBorder = css`
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 0;
  padding: 0em 1em;
  position: relative;
  inset-inline-start: -0.6em;
`

const overlayCss = css`
  z-index: 2;
`

const getFlexItem = (width1, width2, width3) => {
  return css`
    word-break: break-all;
    width: 100%;
    ${mq[0]} {
      width: ${width1}%;
    }
    ${mq[1]} {
      width: ${width2}%;
    }
    ${mq[2]} {
      width: ${width3}%;
    }
  `
}

const labelStyle = css`
  color: ${colors.gray60};
  font-size: 0.75rem;
  padding-top: 0;
  padding-inline-end: 0.75em;
  padding-bottom: 0;
  padding-inline-start: 0;
`
const detailStyle = css`
  font-size: 0.9rem;
`

const MetadataDialogContent = ({ detail, ...overlay }) => (
  <span css={overlayCss}>
    <DialogOverlay {...overlay} size="lg">
      <JSONTable jsonContent={detail} />
    </DialogOverlay>
  </span>
)

export default function DmcDataView({ id, data, columns = 3, expanderDetails, noBorder }) {
  const flexContainer = useMemo(
    () => css`
      display: flex;
      flex-wrap: wrap;
      margin-bottom: 0;
      padding: 0.5em 1em;
      border: ${noBorder ? 'none' : '1px solid #d7d7d7'};
      box-shadow: ${noBorder ? 'none' : '0px 1px #bfbfbf'};
      border-radius: 5px;
    `,
    [noBorder]
  )
  const { t } = useTranslation()
  const [open, setOpen] = useState()

  const checkSelectOrClick = () => {
    // Only trigger the click event if text is not selected
    if (window.getSelection()?.toString().length === 0) {
      setOpen((prevState) => !prevState)
    }
    if (expanderDetails.dataList.length === 0) {
      expanderDetails.expandCallbackFn()
    }
  }

  if (data === null || data === undefined) return <>{t('error.nothingToDisplay', 'Nothing to display!')}</>
  let flexCss
  switch (columns) {
    case 3:
      flexCss = getFlexItem(50, 33, 33)
      break
    case 4:
      flexCss = getFlexItem(33, 25, 25)
      break
    default:
      flexCss = null
      break
  }

  const getMappedData = (dataItems) => {
    return dataItems
      ?.filter((item) => item)
      ?.map((item) => {
        const label =
          item.label === null || item.label?.length === 0 ? null : <span css={labelStyle}>{item.label}:</span>
        let detail = null
        if (item.copyValue) {
          detail = <DmcCopyToClipboardTooltip value={item.copyValue} body={item.detail} nextToText />
        } else {
          detail = item.detail
        }
        return (
          <div
            style={
              expanderDetails?.dataList
                ? { paddingTop: '5px', paddingBottom: '5px', paddingInlineStart: '5px', paddingInlineEnd: '5px' }
                : { paddingInlineStart: '5px', paddingInlineEnd: '5px' }
            }
            css={flexCss}
            key={item.key}
          >
            {label}
            <span data-testid={item.label} css={detailStyle}>
              {typeof detail === 'object' &&
              !React.isValidElement(detail) &&
              detail !== null &&
              detail.constructor === Object ? (
                <MoreMetadataButton MetadataDialog={MetadataDialogContent} detail={detail} />
              ) : (
                detail
              )}
            </span>
          </div>
        )
      })
  }

  const expanderHeader = expanderDetails?.label ? (
    <>
      <div css={flexContainerNoBorder}>{getMappedData(data)}</div>

      <span css={expanderDetails.labelStyle}>
        <ExpanderHeader onClick={checkSelectOrClick}>{expanderDetails.label}</ExpanderHeader>
      </span>
    </>
  ) : (
    <ExpanderHeader onClick={checkSelectOrClick}>
      <div css={flexContainerNoBorder}>{getMappedData(data)}</div>
    </ExpanderHeader>
  )

  return expanderDetails?.dataList ? (
    <div id={id} css={border}>
      <Expander open={open} setOpen={setOpen} gutters="lg" marginY={null}>
        {expanderHeader}
        <ExpanderContent>
          {expanderDetails.dataList.map((dataGroup) => {
            const groupKey = dataGroup?.map((item) => item?.label).join('|')
            return (
              <React.Fragment key={groupKey}>
                <Divider />
                <div css={flexContainerNoBorder}>{getMappedData(dataGroup)}</div>
              </React.Fragment>
            )
          })}
        </ExpanderContent>
      </Expander>
    </div>
  ) : (
    <div id={id}>
      <div css={flexContainer}>{getMappedData(data)}</div>
    </div>
  )
}
const dataItemShape = PropTypes.shape({
  key: PropTypes.string.isRequired,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  detail: PropTypes.oneOfType([PropTypes.node, PropTypes.string, PropTypes.object]),
  copyValue: PropTypes.string,
})

DmcDataView.propTypes = {
  id: PropTypes.string,
  data: PropTypes.arrayOf(dataItemShape),
  columns: PropTypes.number,
  noBorder: PropTypes.bool,
  expanderDetails: PropTypes.shape({
    label: PropTypes.node,
    labelStyle: PropTypes.shape({}),
    expandCallbackFn: PropTypes.func,
    dataList: PropTypes.arrayOf(PropTypes.arrayOf(dataItemShape)),
  }),
}

export { breakpoints }
